import { lazy, Suspense } from 'react';
import { Outlet, createRoute } from '@tanstack/react-router';
import { GuestGuard, RegisterPageGuard } from '@emfood/shared';

import AuthClassicLayout from 'src/layouts/auth/classic';

import { SplashScreen } from 'src/components/loading-screen';

import { paths } from '../paths';
import { rootRoute } from './root';
import { rolesIndexRouteConfig } from 'src/api/roles-config';

// Lazy load components
const JwtLoginPage = lazy(() => import('src/sections/auth/jwt/jwt-login-view'));
const JwtRegisterPage = lazy(() => import('src/sections/auth/jwt/jwt-register-view'));
const LoginCallbackPage = lazy(() => import('src/sections/auth/jwt/oauth-callback-view'));

// Create auth route (pathless/layout route)
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth',
});

// Create JWT route (pathless/layout route for JWT-specific routes)
const jwtRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'jwt',
  component: () => (
    <Suspense fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  ),
});

// Create login route
const loginRoute = createRoute({
  getParentRoute: () => jwtRoute,
  path: 'login',
  component: () => (
    <GuestGuard splashScreen={<SplashScreen />} returnToPath={paths.dashboard.products.root}>
      <AuthClassicLayout title="Welcome">
        <JwtLoginPage />
      </AuthClassicLayout>
    </GuestGuard>
  ),
});

// Create callback route
const callbackRoute = createRoute({
  getParentRoute: () => jwtRoute,
  path: 'callback',
  component: () => (
    <AuthClassicLayout>
      <LoginCallbackPage />
    </AuthClassicLayout>
  ),
});

// Create register route
const registerRoute = createRoute({
  getParentRoute: () => jwtRoute,
  path: 'register',
  component: () => (
    <RegisterPageGuard rolesRouteConfig={rolesIndexRouteConfig}>
      <AuthClassicLayout title="Welcome">
        <JwtRegisterPage />
      </AuthClassicLayout>
    </RegisterPageGuard>
  ),
});

// Build the route tree
const authRouteTree = authRoute.addChildren([
  jwtRoute.addChildren([loginRoute, callbackRoute, registerRoute]),
]);

export { authRouteTree };
